import { lessonStatusFromApi } from "@/components/course/tree/types";
import type { ProgressModule } from "@/api/generated/model";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { AccountInfo } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { getEnrollmentCourseStructure } from "@/api/generated/enrollments/enrollments";
import { useEnrollment } from "@/hooks/useEnrollment";
import { LoadingState } from "@/components/common/LoadingState";
import { ErrorState } from "@/components/common/ErrorState";
import { CourseCompletionQuizCard } from "@/components/courseCompletionQuiz/CourseCompletionQuizCard";
import {
  ContinueCard,
  courseIsCompleted,
} from "@/components/dashboard/ContinueLearningCard";
import type { ModuleProgressInfo } from "@/components/dashboard/ModuleRow";
import { moduleStatusFromProgress } from "@/components/dashboard/moduleStatusFromProgress";
import { unveiledChapterForCheckedIntro } from "@/components/dashboard/unveiledChapterForCheckedIntro";
import { LearningGroupCard } from "@/components/dashboard/LearningGroupCard";
import { TrainerCard } from "@/components/dashboard/TrainerCard";
import { useCourseCompletionQuizDetails } from "@/hooks/courseCompletionQuiz/useCourseCompletionQuizDetails";
import { buildIntroUnveilQueryString } from "@/lib/chapterNavigation";
import { courseCompletionQuizPath } from "@/lib/courseCompletionQuiz";
import {
  toLoadedCourse,
  useCourseDetailStore,
} from "@/stores/courseDetailStore";
import { useSectionCompletionStore } from "@/stores/sectionCompletionStore";

type ModuleDashboardTarget = Pick<ProgressModule, "id" | "nextSubchapterId">;

const resolveModuleDashboardPath = (
  enrollmentId: string,
  status: ModuleProgressInfo["status"],
  moduleProgress: ModuleDashboardTarget,
  checkedChapterId: string | null
): string => {
  if (status === "not_started" && moduleProgress.id) {
    if (checkedChapterId) {
      return `/course/${enrollmentId}/${checkedChapterId}${buildIntroUnveilQueryString("")}`;
    }

    return `/course/${enrollmentId}/${moduleProgress.id}`;
  }

  if (moduleProgress.nextSubchapterId) {
    return `/course/${enrollmentId}/${moduleProgress.nextSubchapterId}`;
  }

  return `/course/${enrollmentId}`;
};

const resolveLearnerFirstName = (
  enrollmentFirstName: string | null | undefined,
  accounts: AccountInfo[]
): string => {
  if (enrollmentFirstName) return enrollmentFirstName;

  const account = accounts[0];
  const claims = account?.idTokenClaims as Record<string, unknown> | undefined;

  if (typeof claims?.given_name === "string" && claims.given_name) {
    return claims.given_name;
  }

  const accountName = account?.name?.split(" ")[0];

  if (accountName) return accountName;

  return "";
};

export const DashboardParticipant = () => {
  const navigate = useNavigate();
  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const { accounts } = useMsal();

  const {
    courseTitle,
    enrollmentId: loadedEnrollmentId,
    courseData,
  } = useCourseDetailStore();
  const completedSections = useSectionCompletionStore(
    (state) => state.completed
  );

  const courseTreeLoaded =
    loadedEnrollmentId === enrollmentId && courseData != null;
  const courseStructure = courseTreeLoaded ? courseData?.structure : undefined;

  const {
    data: enrollment,
    error,
    isLoading: loading,
  } = useEnrollment(enrollmentId);

  const { lastResult } = useCourseCompletionQuizDetails(
    enrollmentId,
    enrollment?.courseCompletionQuiz?.status
  );

  // Set the course title in the store so the Header can display it,
  // without triggering a full course structure fetch.
  const showCourseTitleInHeader = () => {
    if (!enrollment) return;

    useCourseDetailStore.setState({ courseTitle: enrollment.courseTitle });
  };
  useEffect(showCourseTitleInHeader, [enrollment]);

  const loadCourseTreeWhenDashboardHasNone = () => {
    if (!enrollmentId || courseTreeLoaded) return;

    let ignoreResult = false;

    getEnrollmentCourseStructure(enrollmentId)
      .then((structure) => {
        if (ignoreResult) return;

        useCourseDetailStore.setState({
          enrollmentId,
          courseData: toLoadedCourse(structure),
        });
      })
      .catch(() => undefined);

    return () => {
      ignoreResult = true;
    };
  };
  useEffect(loadCourseTreeWhenDashboardHasNone, [
    courseTreeLoaded,
    enrollmentId,
  ]);

  if (!enrollmentId) {
    return (
      <ErrorState
        title="Dashboard konnte nicht geladen werden"
        message="Keine Einschreibungs-ID in der URL"
      />
    );
  }

  if (loading) {
    return <LoadingState message="Kurse werden geladen..." />;
  }

  if (error) {
    return (
      <ErrorState
        title="Dashboard konnte nicht geladen werden"
        message={error.message}
      />
    );
  }

  if (!enrollment) return null;

  const progress = Math.round(enrollment.progress?.progressPercent ?? 0);
  const isCompleted = courseIsCompleted(enrollment.status, progress);
  const isReturningUser = progress > 0 && !isCompleted;
  const hasStartedCourse = progress > 0;

  const quiz = enrollment.courseCompletionQuiz ?? null;

  const firstName = resolveLearnerFirstName(enrollment.userFirstName, accounts);

  const moduleData = () => {
    const progressData = enrollment.progress;
    const enrollmentModules = progressData?.modules;

    if (!progressData || !enrollmentModules?.length) return null;

    const lessonStatusById = Object.fromEntries(
      progressData.subchapters.map((subchapter) => [
        subchapter.id,
        { status: lessonStatusFromApi(subchapter.status) },
      ])
    );

    const moduleProgressItems: ModuleProgressInfo[] = enrollmentModules.map(
      (moduleProgress) => {
        const status = moduleStatusFromProgress(moduleProgress.progressPercent);

        const checkedChapterId = moduleProgress.id
          ? unveiledChapterForCheckedIntro(
              moduleProgress.id,
              courseStructure,
              completedSections,
              lessonStatusById
            )
          : null;

        const targetPath = resolveModuleDashboardPath(
          enrollmentId,
          status,
          moduleProgress,
          checkedChapterId
        );

        return {
          title: moduleProgress.title,
          chapterCount: moduleProgress.chapters.length,
          progressPercent: moduleProgress.progressPercent,
          status,
          onClick: () => navigate(targetPath),
        };
      }
    );

    const totalModulesComplete = enrollmentModules.filter(
      (moduleProgress) =>
        moduleStatusFromProgress(moduleProgress.progressPercent) === "completed"
    ).length;

    return {
      modules: moduleProgressItems,
      totalModulesComplete,
      totalModulesOpen: enrollmentModules.length - totalModulesComplete,
    };
  };

  const breadcrumb = () => {
    if (!isReturningUser) return null;

    const enrollmentProgress = enrollment.progress;

    if (
      !enrollmentProgress?.nextLessonModuleIndex ||
      !enrollmentProgress.nextLessonChapterIndex ||
      !enrollmentProgress.nextLessonTitle
    ) {
      return null;
    }

    return `Modul ${enrollmentProgress.nextLessonModuleIndex}\u00A0\u00A0›\u00A0\u00A0Kapitel ${enrollmentProgress.nextLessonChapterIndex}\u00A0\u00A0›\u00A0\u00A0${enrollmentProgress.nextLessonTitle}`;
  };

  const renderQuizCard = () => {
    if (!quiz || quiz.status === "locked") return null;

    return (
      <CourseCompletionQuizCard
        summary={quiz}
        courseTitle={courseTitle ?? enrollment.courseTitle}
        progressPercent={progress}
        lastResult={lastResult}
        onAction={() =>
          navigate(courseCompletionQuizPath(enrollmentId, quiz.status))
        }
      />
    );
  };

  const continueLearning = () => {
    if (!hasStartedCourse) {
      navigate(`/course/${enrollmentId}`);
      return;
    }

    const { nextLessonId: lessonId, nextContentElementId: elementId } =
      enrollment.progress ?? {};
    const canContinueLesson = Boolean(lessonId && elementId);

    if (canContinueLesson) {
      navigate(`/course/${enrollmentId}/${lessonId}/${elementId}`);
      return;
    }

    if (lessonId) {
      navigate(`/course/${enrollmentId}/${lessonId}`);
      return;
    }

    navigate(`/course/${enrollmentId}`);
  };

  return (
    <div className="min-h-full">
      <div className="mx-auto grid max-w-5xl grid-cols-1 items-start gap-6 lg:grid-cols-[1fr_300px] px-4 pt-4 md:px-8 md:pt-6">
        <div>
          <ContinueCard
            courseTitle={courseTitle ?? enrollment.courseTitle}
            progress={progress}
            firstName={firstName}
            isReturningUser={isReturningUser}
            isCompleted={isCompleted}
            breadcrumb={breadcrumb()}
            onClick={continueLearning}
            afterGreeting={renderQuizCard()}
            {...(moduleData() ?? {})}
          />
        </div>
        <aside className="flex flex-col gap-6">
          <TrainerCard enrollmentId={enrollmentId} />
          <LearningGroupCard enrollmentId={enrollmentId} />
        </aside>
      </div>
    </div>
  );
};
